import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { listStories } from "../api/stories";
import { InkDivider } from "../components/InkDivider";
import { RisographBackground } from "../components/RisographBackground";
import { StoryCard } from "../components/StoryCard";
import type { StorySortOrder } from "../types";

function SkeletonCard() {
	return (
		<div
			className="w-full border border-ink/10 rounded-sm pl-5 pr-6 py-5 animate-pulse"
			style={{ borderLeft: "3px solid rgba(26,18,9,0.15)" }}
		>
			<div className="flex items-start justify-between gap-4 mb-2">
				<div className="h-6 bg-ink/10 rounded w-2/3" />
				<div className="h-5 bg-ink/10 rounded w-16 shrink-0" />
			</div>
			<div className="h-3 bg-ink/10 rounded w-1/4 mb-3" />
			<div className="h-4 bg-ink/10 rounded w-full mb-1.5" />
			<div className="h-4 bg-ink/10 rounded w-4/5" />
		</div>
	);
}

export function StoriesListPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [sort, setSort] = useState<StorySortOrder>("newest");
	const [activeTag, setActiveTag] = useState<string | null>(null);
	const [page, setPage] = useState(1);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, sort, activeTag]);

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ["stories", page, sort, debouncedSearch, activeTag],
		queryFn: () =>
			listStories({
				page,
				limit: 20,
				sort,
				search: debouncedSearch || undefined,
				tag: activeTag || undefined,
			}),
		placeholderData: keepPreviousData,
	});

	const stories = data?.stories ?? [];
	const allTags = data?.all_tags ?? [];
	const totalPages = data?.pages ?? 1;
	const total = data?.total ?? 0;

	return (
		<div className="min-h-screen px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 gap-3 sm:gap-0">
				<h1 className="font-display text-5xl font-bold text-ink leading-tight">
					{t("stories.list.headline")}
				</h1>
				<div className="flex sm:flex-col items-start sm:items-end gap-3 sm:pt-2">
					<button
						type="button"
						onClick={() => navigate("/stories/new")}
						className="ink-btn px-5 py-2.5 font-ui text-xs uppercase tracking-widest"
					>
						{t("stories.list.beginNew")}
					</button>
				</div>
			</div>

			<InkDivider className="mb-6 mt-4" />

			<div className="flex items-center gap-3 mb-6">
				<input
					type="search"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder={t("stories.list.searchPlaceholder")}
					className="flex-1 bg-transparent border border-ink/20 rounded-sm px-4 py-2 font-ui text-xs uppercase tracking-widest text-ink placeholder:text-ink/30 focus:outline-none focus:border-ink/50 transition-colors"
				/>
				<button
					type="button"
					onClick={() => setSort(sort === "newest" ? "oldest" : "newest")}
					className="font-ui text-[10px] uppercase tracking-widest px-3 py-2 border border-ink/20 rounded-sm text-ink/50 hover:border-ink/40 hover:text-ink/70 transition-colors whitespace-nowrap"
				>
					{t(
						sort === "newest"
							? "stories.list.sortNewest"
							: "stories.list.sortOldest",
					)}
				</button>
			</div>

			{isLoading && (
				<div className="space-y-4">
					<SkeletonCard />
					<SkeletonCard />
					<SkeletonCard />
				</div>
			)}

			{isError && (
				<p className="font-ui text-sm uppercase tracking-wide text-coral">
					{t("stories.list.errorLoad")}
				</p>
			)}

			{!isLoading && !isError && (
				<div
					className={
						isFetching
							? "opacity-60 transition-opacity duration-150"
							: "transition-opacity duration-150"
					}
				>
					{allTags.length > 0 && (
						<div className="mb-6">
							<div className="flex items-center justify-between mb-2">
								<span className="font-ui text-[10px] uppercase tracking-widest text-ink/40">
									{t("stories.list.filterBy")}
								</span>
								{activeTag && (
									<button
										type="button"
										onClick={() => setActiveTag(null)}
										className="font-ui text-[10px] uppercase tracking-widest text-ink/50 hover:text-ink/80 transition-colors"
									>
										{t("stories.list.clearFilter")}
									</button>
								)}
							</div>
							<div className="flex flex-wrap gap-2">
								{allTags.map((tag) => (
									<button
										key={tag}
										type="button"
										aria-pressed={activeTag === tag}
										onClick={() => setActiveTag(activeTag === tag ? null : tag)}
										className={`font-ui text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm border transition-colors ${
											activeTag === tag
												? "border-ink/60 text-ink bg-white/60"
												: "border-ink/20 text-ink/50 hover:border-ink/40 hover:text-ink/70"
										}`}
									>
										{tag}
									</button>
								))}
							</div>
						</div>
					)}

					{total > 0 && (
						<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-4">
							{activeTag
								? t("stories.list.filteredCount", {
										filtered: total,
										total: data?.total ?? 0,
									})
								: t("stories.list.storiesCount", { count: total })}
						</p>
					)}

					{total === 0 && !debouncedSearch && !activeTag ? (
						<div className="text-center py-20">
							<p className="font-body italic text-ink/50 text-xl mb-6">
								{t("stories.list.empty")}
							</p>
							<button
								type="button"
								onClick={() => navigate("/stories/new")}
								className="ink-btn px-6 py-3 font-ui text-sm uppercase tracking-widest"
							>
								{t("stories.list.beginNew")}
							</button>
						</div>
					) : total === 0 ? (
						<div className="space-y-3">
							<p className="font-body italic text-ink/50 text-lg">
								{activeTag
									? t("stories.list.noTag", { tag: activeTag })
									: t("stories.list.noResults")}
							</p>
							<button
								type="button"
								onClick={() => {
									setActiveTag(null);
									setSearch("");
								}}
								className="font-ui text-xs uppercase tracking-widest text-ink/50 hover:text-ink/80 transition-colors"
							>
								{t("stories.list.showAll")}
							</button>
						</div>
					) : (
						<div className="space-y-4">
							{stories.map((story) => (
								<StoryCard key={story.id} story={story} />
							))}
						</div>
					)}

					{totalPages > 1 && (
						<div className="flex items-center justify-between mt-8">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1}
								className="font-ui text-[10px] uppercase tracking-widest px-4 py-2 border border-ink/20 rounded-sm text-ink/50 hover:border-ink/40 hover:text-ink/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
							>
								←
							</button>
							<span className="font-ui text-[10px] uppercase tracking-widest text-ink/40">
								{t("stories.list.pageOf", { page, pages: totalPages })}
							</span>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page === totalPages}
								className="font-ui text-[10px] uppercase tracking-widest px-4 py-2 border border-ink/20 rounded-sm text-ink/50 hover:border-ink/40 hover:text-ink/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
							>
								→
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
